// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { getTotalGasUsed } from '@mysten/core';
import { X12, Dot12 } from '@safecoin/icons';
import { type SafeJsonRpcClient, type SafeTransactionBlockResponse } from '@safecoin/safe/jsonRpc';
import { Text } from '@mysten/ui';

import { SafeAmount } from '../Table/SafeAmount';
import { SenderLabel } from '../SenderLabel';
import { TxTimeType } from '../tx-time/TxTimeType';
import { HighlightedTableCol } from '~/components/Table/HighlightedTableCol';
import { TransactionLink } from '~/ui/InternalLink';

// Derive the "Type / Func" cell: the transaction kind, plus the leading Move
// call's function name and how many extra commands the PTB carries.
function getTypeFunc(transaction: SafeTransactionBlockResponse) {
	const inner = transaction.transaction?.data.transaction as
		| { kind?: string; transactions?: unknown[] }
		| undefined;
	const kind = inner?.kind;
	const isProgrammable = kind === 'ProgrammableTransaction' || kind === 'ProgrammableSystemTransaction';
	if (!isProgrammable) {
		return { kindLabel: kind ?? 'Transaction', func: null as string | null, extra: 0 };
	}
	const commands = inner?.transactions ?? [];
	const firstMoveCall = commands.find(
		(c) => !!c && typeof c === 'object' && 'MoveCall' in c,
	) as { MoveCall?: { function?: string } } | undefined;
	return {
		kindLabel: 'Programmable Tx',
		func: firstMoveCall?.MoveCall?.function ?? null,
		extra: Math.max(commands.length - 1, 0),
	};
}

// Generate table data from the transaction data
export const genTableDataFromTxData = (results: SafeTransactionBlockResponse[]) => ({
	data: results.map((transaction) => {
		const status = transaction.effects?.status.status;
		const sender = transaction.transaction?.data.sender;
		const { kindLabel, func, extra } = getTypeFunc(transaction);

		return {
			typeFunc: (
				<HighlightedTableCol>
					<div className="flex flex-col gap-0.5">
						<Text variant="bodySmall/medium" color="steel-darker">
							{kindLabel}
						</Text>
						{func && (
							<div className="flex items-center gap-1.5">
								<span className="text-captionSmall font-medium text-success-dark">{func}</span>
								{extra > 0 && (
									<span className="rounded bg-safe/10 px-1.5 text-captionSmall font-medium text-hero-dark">
										{extra}
									</span>
								)}
							</div>
						)}
					</div>
				</HighlightedTableCol>
			),
			date: (
				<HighlightedTableCol>
					<TxTimeType timestamp={Number(transaction.timestampMs || 0)} />
				</HighlightedTableCol>
			),
			digest: (
				<HighlightedTableCol first>
					<TransactionLink
						digest={transaction.digest}
						before={
							status === 'success' ? (
								<Dot12 className="text-success" />
							) : (
								<X12 className="text-issue-dark" />
							)
						}
					/>
				</HighlightedTableCol>
			),
			txns: (
				<div>
					{transaction.transaction?.data.transaction.kind === 'ProgrammableTransaction'
						? transaction.transaction.data.transaction.transactions.length
						: '--'}
				</div>
			),
			gas: <SafeAmount amount={transaction.effects && getTotalGasUsed(transaction.effects!)} />,
			sender: (
				<HighlightedTableCol>
					<SenderLabel address={sender} />
				</HighlightedTableCol>
			),
		};
	}),
	columns: [
		{
			header: 'Type / Func',
			accessorKey: 'typeFunc',
		},
		{
			header: 'Digest',
			accessorKey: 'digest',
		},
		{
			header: 'Sender',
			accessorKey: 'sender',
		},
		{
			header: 'Txns',
			accessorKey: 'txns',
		},
		{
			header: 'Gas',
			accessorKey: 'gas',
		},
		{
			header: 'Time',
			accessorKey: 'date',
		},
	],
});

const dedupe = (arr: string[]) => Array.from(new Set(arr));

export const getDataOnTxDigests = (client: SafeJsonRpcClient, transactions: string[]) =>
	client
		.multiGetTransactionBlocks({
			digests: dedupe(transactions),
			options: {
				showInput: true,
				showEffects: true,
				showEvents: true,
			},
		})
		.then((transactions) =>
			// Remove failed transactions
			transactions.filter((item) => item),
		);
